import type { CompleteBookingInput, HoldResponse } from '@seaa/shared'

export interface BookingDraft {
  slug: string
  players: number
  hold: HoldResponse | null
  customer: CompleteBookingInput['customer'] | null
  customerUserId: string | null
  paymentMode: CompleteBookingInput['paymentMode']
}

/** Only the reservation survives a reload. Contact and identity must be rechecked. */
export function persistedBookingDraft(draft: BookingDraft) {
  return { slug: draft.slug, players: draft.players, hold: draft.hold, paymentMode: draft.paymentMode }
}

export function restoredBookingDraft(saved: unknown): BookingDraft {
  const value = saved && typeof saved === 'object' ? saved as Partial<BookingDraft> : {}
  return {
    slug: typeof value.slug === 'string' ? value.slug : '',
    players: typeof value.players === 'number' && value.players > 0 ? value.players : 1,
    hold: value.hold && typeof value.hold.holdToken === 'string' ? value.hold : null,
    paymentMode: ['deposit', 'full', 'pay_later', 'member_credit'].includes(value.paymentMode ?? '') ? value.paymentMode! : 'deposit',
    customer: null,
    customerUserId: null,
  }
}

export function customerForSession(draft: BookingDraft, userId: string | null) {
  return userId && userId === draft.customerUserId ? draft.customer : null
}

export function updateBookingDraft<T extends BookingDraft>(current: T, next: Partial<T>): T {
  const changedSelection = (next.slug !== undefined && next.slug !== current.slug)
    || (next.hold !== undefined && next.hold?.holdToken !== current.hold?.holdToken)
  return { ...current, ...(changedSelection ? { customer: null, customerUserId: null } : {}), ...next }
}

export interface RecentBookingAccess { bookingCode: string; manageToken: string; userId: string }

/** Legacy unbound bearer tokens must not follow the next person using this browser. */
export function restoredBookingAccess(value: unknown): RecentBookingAccess | null {
  if (!value || typeof value !== 'object') return null
  const access = value as Partial<RecentBookingAccess>
  return typeof access.userId === 'string' && access.userId.length > 0
    && typeof access.bookingCode === 'string' && typeof access.manageToken === 'string'
    ? access as RecentBookingAccess : null
}

export function bookingAccessForSession(access: RecentBookingAccess | null, userId: string | null) {
  return userId && access?.userId === userId ? access : null
}
